import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
    ReactiveDropdownSyncController,
    ReactiveFormSupportDataExecutor,
    type ReactiveFormLoadPlan,
} from '@fitogest/reactive-form-builder/utils';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { EntidadesApiService } from '../../../../../service/api/entidades-api.service';
import { ProductosApiService } from '../../../../../service/api/productos-api';
import { TransaccionesApiService } from '../../../../../service/api/transacciones-api.service';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { BaseForm } from '../../../../../view/base-form';
import { environment } from '../../../../../../environments/environment';
import { StorageManager } from '../../../../../common/storage-manager.class';
import {
    FormType,
    ReactiveFormChangedField,
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { ClienteModel } from 'src/app/models/cliente.model';
import { ProductosModel } from 'src/app/models/productos/productos.model';
import { EntidadModel } from 'src/app/models/reto/entidad.model';
import { TransaccionModel } from 'src/app/models/reto/transaccion.model';
import { TransaccionesFormDefinition } from './form-control/form-definition';
import { RequestHandler } from 'src/app/service/OffService/request-handler';
import { TransaccionesSupportDataSourcesFactory } from './transacciones-support-data-sources.factory';

@Component({
    selector: 'app-transacciones-form',
    standalone: false,
    templateUrl: './transacciones-form.component.html',
    styleUrls: ['./transacciones-form.component.scss'],
})
export class TransaccionesFormComponent extends BaseForm<TransaccionModel> implements OnInit, OnDestroy {
    public form!: TransaccionesFormDefinition;

    public appName = environment.appName;

    public hasClients = environment.features.hasClients;

    public fincaName = environment.features.fincaName;

    public fincasName = environment.features.fincasName;

    public showReto = environment.features.showReto;

    public override model = { fecha: '' } as TransaccionModel;

    public error = '';

    public reactiveFormRecordId = '';

    public getRequest!: RequestHandler<TransaccionModel[]>;

    public loadingData = true;

    private reactiveFormLoadPlan: ReactiveFormLoadPlan | null = null;

    private products: ProductosModel[] = [];

    private entities: EntidadModel[] = [];

    public constructor(
        public override router: Router,
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public transaccionesApi: TransaccionesApiService,
        private readonly productosApi: ProductosApiService,
        public entidadesApi: EntidadesApiService,
        public clientesApi: ClientesApiService,
    ) {
        super(
            route,
            router,
            dashboard,
            transaccionesApi.TransaccionesPUT,
            transaccionesApi.TransaccionesPOST,
            'transacciones',
            'Editar transacción',
            'Crear transacción',
            'Duplicar transacción',
        );

        this.getRequest = this.getType() === FormType.DUPLICATION
            ? this.transaccionesApi.TransaccionesGET_GENERIC
            : this.transaccionesApi.TransaccionesGET;
    }// constructor();

    public async ngOnInit(): Promise<void> {
        this.resetExternalDropdowns();
        const routeModel = this.getModel() as Partial<TransaccionModel>;

        this.reactiveFormRecordId = this.resolveReactiveFormRecordId(routeModel);
        this.form = new TransaccionesFormDefinition(
            this.getType(),
            this.initializeHostModel({ ...this.model, ...routeModel } as TransaccionModel),
        );
        this.configureTransaccionesSupportDataSources();
        this.updateHostModel(this.form.model as TransaccionModel);
        this.createTransaccionesFormLoadPlan();

        await this.initFormData();
        this.softInit(this.getType());
    }// ngOnInit();

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
    }// ngOnDestroy();

    public stableValueChanges(event: ReactiveFormStableValueChange<TransaccionModel>): void {
        this.captureStableValueChange(event.value as TransaccionModel);
        this.form.updateModel(event.value, event.changedField);
        this.updateHostModel(this.form.model as TransaccionModel);
        this.handleReactiveValueChange(String(event.changedField ?? ''));
    }// stableValueChanges();

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<TransaccionModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            return;
        }// if();

        const modelChanges = this.captureStableValueChange(event.value as TransaccionModel);
        const hydrationField = '__previous_data_patch' as ReactiveFormChangedField<TransaccionModel>;

        this.form.updateModel(modelChanges as ReactiveFormValue<TransaccionModel>, hydrationField);
        this.updateHostModel(this.form.model as TransaccionModel);
        this.handleReactiveValueChange(String(hydrationField));
    }// onReactiveFormLifecycleEvent();

    private handleReactiveValueChange(tag: string): void {
        let shouldRefreshDropdowns = false;

        if (tag === 'descuadre' && this.form.model.descuadre === true) {
            this.form.model.almacen_origen = '';
            this.form.model.almacen_destino = '';
        }// if();

        if ((tag === 'almacen_origen' || tag === 'almacen_destino') && this.hasWarehouseNumber(tag)) {
            this.form.model.descuadre = false;
        }// if();

        if (tag === 'id_entidad_origen') {
            this.form.model.ropo_origen = '';
            this.fillRopoOrigen();
            shouldRefreshDropdowns = true;
        }// if();

        if (tag === 'id_entidad_destino') {
            this.form.model.ropo_destino = '';
            this.fillRopoDestino();
            shouldRefreshDropdowns = true;
        }// if();

        if (tag === 'tipo_transaccion') {
            this.prepareDestinationForTransactionType(true);
            shouldRefreshDropdowns = true;
        }// if();

        if (tag === '__previous_data_patch' || tag === '__dropdown_refresh') {
            this.prepareDestinationForTransactionType(false);
            this.updateHostModel(this.form.model as TransaccionModel);
            shouldRefreshDropdowns = tag === '__previous_data_patch';
        }// if();

        if (
            tag === 'id_entidad_origen' ||
            tag === 'ropo_origen' ||
            tag === 'tipo_transaccion' ||
            tag === 'id_entidad_destino' ||
            tag === 'ropo_destino' ||
            tag === '__previous_data_patch' ||
            tag === '__dropdown_refresh'
        ) {
            this.syncConditionalSections();
        }// if();

        if (tag === 'num_envases' || tag === 'capacidad_envase') {
            this.form.calculateTotalVolume();
        }// if();

        if (tag === 'id_producto' || tag === '__previous_data_patch' || tag === '__dropdown_refresh') {
            this.form.syncSelectedProductUnits(this.products);
        }// if();

        this.updateHostModel(this.form.model as TransaccionModel);
        this.form.refreshSections();
        this.formBuilderRef?.updateForm(this.form.model as Record<string, unknown>, false);

        if (shouldRefreshDropdowns) {
            this.refreshReactiveDropdownMappings();
        }// if();
    }// handleReactiveValueChange();

    public override goBack(): void {
        this.request.reset();
        this.router.navigate(['dashboard', 'reto-transacciones']);
    }// goBack();

    private async initFormData(): Promise<void> {
        try {
            await ReactiveFormSupportDataExecutor.load({
                form: this.form.formFields,
                loadPlan: this.reactiveFormLoadPlan,
                phase: 'critical',
                setLoading: (loading) => {
                    this.loadingData = loading;
                },
            });

            this.fillProducts();
            this.fillEntities();
            this.prepareDestinationForTransactionType(false);
            this.updateHostModel(this.form.model as TransaccionModel);
        } catch (error) {
            this.error = 'No se han podido cargar los datos auxiliares de transacciones.';
            console.error(this.error, error);
        } finally {
            this.loadingData = false;
            this.form.refreshSections();
            this.formBuilderRef?.updateForm(this.form.model as Record<string, unknown>, false);
            this.finalizeExternalDropdowns();
            this.refreshReactiveDropdownMappings();
        }// try();
    }// initFormData();

    private configureTransaccionesSupportDataSources(): void {
        this.form.formFields.supportDataSources = TransaccionesSupportDataSourcesFactory.create({
            form: this.form,
            hasClients: this.hasClients,
            endpoints: {
                productosGET: this.productosApi.productos.GET,
                entidadesGET: this.entidadesApi.EntidadesGET,
                clientesGET: this.clientesApi.clientesGET,
            },
            setProducts: (products) => {
                this.products = products;
            },
            setEntities: (entities) => {
                this.entities = entities;
            },
        });
    }// configureTransaccionesSupportDataSources();

    private createTransaccionesFormLoadPlan(): void {
        this.reactiveFormLoadPlan = this.createReactiveFormLoadPlan(this.form.formFields);
    }// createTransaccionesFormLoadPlan();

    private refreshReactiveDropdownMappings(): void {
        ReactiveDropdownSyncController.refreshMappings({ formBuilder: this.formBuilderRef });
    }// refreshReactiveDropdownMappings();

    private fillProducts(): void {
        this.form.setProductOptions(this.products);
    }// fillProducts();

    private fillEntities(): void {
        this.form.setEntityOptions(this.entities.filter((entity) => this.canUseEntity(entity)));
    }// fillEntities();

    private fillRopoOrigen(): void {
        this.form.setRopoOrigenFromEntity(this.findEntityById(this.form.model.id_entidad_origen));
    }// fillRopoOrigen();

    private fillRopoDestino(): void {
        if (String(this.form.model.tipo_transaccion ?? '') === '8' && environment.features.hasClients) {
            this.form.setRopoDestinoFromClient(this.findClientById(this.form.model.id_entidad_destino));
            return;
        }// if();

        this.form.setRopoDestinoFromEntity(this.findEntityById(this.form.model.id_entidad_destino));
    }// fillRopoDestino();

    private prepareDestinationForTransactionType(shouldClearDestinationRopo: boolean): void {
        if (shouldClearDestinationRopo) {
            this.form.model.ropo_destino = '';
        }// if();

        if (String(this.form.model.tipo_transaccion ?? '') !== '8') {
            delete this.form.model.cultivo_tratamiento;
            this.fillEntities();
        }// if();

        this.fillRopoOrigen();
        this.fillRopoDestino();
        this.form.refreshSections();
    }// prepareDestinationForTransactionType();

    private syncConditionalSections(): void {
        if (!this.form.isWarehouseTransfer()) {
            this.form.deleteWarehouseData();
        }// if();

        this.form.refreshSections();
    }// syncConditionalSections();

    private hasWarehouseNumber(tag: string): boolean {
        const value = this.form.model[tag as keyof TransaccionModel];

        return value !== undefined && value !== null && String(value) !== '';
    }// hasWarehouseNumber();

    private canUseEntity(entity: EntidadModel): boolean {
        const role = StorageManager.getUser().rol ?? '';
        const assignedUserId = StorageManager.getUser().tecnico_asignado ?? '';

        return role === 'admin' || assignedUserId === entity.id_usuario;
    }// canUseEntity();

    private findEntityById(entityId: unknown): EntidadModel | undefined {
        const normalizedId = this.getRecordId(entityId);

        return this.entities.find((entity) => String(entity.id ?? '') === normalizedId);
    }// findEntityById();

    private findClientById(clientId: unknown): ClienteModel | undefined {
        const normalizedId = this.getRecordId(clientId);

        return this.form.lists.clientes.filtered
            .map((clientOption) => clientOption.value ?? undefined)
            .find((client) => String(client?.id ?? '') === normalizedId);
    }// findClientById();

    private getRecordId(value: unknown): string {
        if (typeof value === 'object' && value !== null && 'id' in value) {
            return String((value as { readonly id?: unknown }).id ?? '');
        }// if();

        return String(value ?? '');
    }// getRecordId();
}// class;
